import * as React from "react";
import { Alert, Snackbar } from "@mui/material";
import { ExtendedAccount } from "@/types/ExtendedAccount";
import {
  Account,
  AccountNextStep,
  AccountType,
  ServiceProvider,
  ServiceProviderType,
} from "@/types";
import { cleanedErrors } from "@/utils/Functions";
import { TextInput } from "@/components/atoms/TextInput";
import { FormField } from "@/components/molecules/FormField";
import {
  useOnValueChange,
  useOnChangeWithError,
  useOnValueChangeWithError,
} from "@/utils/callbacks";
import { AccountNextStepInput } from "@/components/molecules";
import { AccountFieldProps } from "../AccountForm";
import { nextActionByQuilterAccountType, QuilterAccountTypeOptions } from "@/types/quilter";
import { BooleanInput, SelectInput, YesNoBooleanRadioField } from "@/components/atoms";
import { NestedErrors } from "@/utils/Types";

export type QuilterRecord = {
  // Head Account Number (replace customer reference number)
  readonly accountNumber?: string;
  // Account Sub Type (drop-down) IndividualSavingsAccount ISA Collective Investment Bond (CIB) Collective Investment Account (CIA) Cash Hub (CHB) Collective Retirement Account - Pension (CRA)
  readonly accountType?: AccountType;
  // Sub Account Number (free text field)
  readonly holderNumber?: string;
  // Required Action (drop-down options from the table below)
  readonly nextSteps?: AccountNextStep;
  // Question:(This Should show up in backend)
  // Do you expect the value of the total assets held to be worth more than £100,000, or did the deceased pass away offshore with total assets exceeding £30,000?// Y/N
  readonly assetsValueAboveThreshold?: boolean;
  readonly isPlaceOfDeathUk?: boolean;
};

export type QuilterErrors = NestedErrors<QuilterRecord>;

export function errorsOfQuilter(record: QuilterRecord): QuilterErrors {
  return cleanedErrors({
    accountNumber: record?.accountNumber ? undefined : "required",
    accountType: record?.accountType ? undefined : "required",
    holderNumber: record?.holderNumber ? undefined : "required",
    nextSteps: record?.nextSteps ? undefined : "required",
    isPlaceOfDeathUk: record?.isPlaceOfDeathUk !== undefined ? undefined : "required",
  });
}

export const quilterRecordFromAccount = (account?: Account): QuilterRecord => {
  if (!account) return {};
  return {
    accountNumber: account.accountNumber,
    accountType: account.accountType,
    holderNumber: account.holderNumber,
    nextSteps: account.nextSteps,
    assetsValueAboveThreshold: account.assetsValueAboveThreshold,
    isPlaceOfDeathUk: account.isPlaceOfDeathUk,
  };
};

export type QuilterAccountFieldsProps = AccountFieldProps;

export const QuilterAccountFields: React.FC<QuilterAccountFieldsProps> = ({
  serviceProvider,
  account,
  setBusy,
  onAccountAdded,
  updateTemplate,
  saveAccount,
  saveRecord,
  savedRecord = false,
}) => {
  // State Management
  const [record, update] = React.useState<QuilterRecord>(
    savedRecord || quilterRecordFromAccount(account)
  );
  const [errors, setErrors] = React.useState<QuilterErrors>({} as QuilterErrors);
  const [remoteErrors, setRemoteErrors] = React.useState(undefined as string | undefined);
  const onAccountNextStepsValueChange = useOnValueChangeWithError<QuilterRecord, QuilterErrors>(
    update,
    setErrors,
    "nextSteps"
  );
  const onChange = useOnChangeWithError(update, setErrors);
  const onAccountTypeValueChange = React.useCallback(
    (value: any) => {
      update((s: any) => ({
        ...s,
        accountType: value,
        nextSteps: undefined,
      }));
    },
    [update]
  );

  const onPlaceOfDeathUkChange = React.useCallback(
    (value: any) => {
      update((s: any) => ({
        ...s,
        isPlaceOfDeathUk: value,
        assetsValueAboveThreshold: false,
      }));
    },
    [update]
  );

  // Send Data to bankend
  React.useEffect(() => {
    if (!updateTemplate) return;
    updateTemplate({
      onNext: !serviceProvider?.serviceProviderType
        ? undefined
        : () => {
            const errors = errorsOfQuilter(record);

            if (errors) {
              setErrors(errors);
              if (saveRecord) {
                saveRecord({ source: record });
              }
              return;
            }

            setBusy(true);

            const data = {
              id: account?.id,
              serviceProvider: {
                id: serviceProvider.serviceProviderId,
                companyName: serviceProvider.customProviderName,
                serviceProviderType: serviceProvider.serviceProviderType,
              },
              accountNumber: record.accountNumber,
              accountType: record.accountType,
              holderNumber: record.holderNumber,
              nextSteps: record.nextSteps,
              nextStep: record.nextSteps,
              assetsValueAboveThreshold: !!record.assetsValueAboveThreshold,
              isPlaceOfDeathUk: record.isPlaceOfDeathUk,
            };

            if (saveRecord) {
              saveRecord({ source: record, target: data });
              return { response: {} };
            }

            return saveAccount({
              sector: ServiceProviderType.Investments,
              record: data,
            }).then(
              (
                response: {
                  data: {
                    account: ExtendedAccount;
                    newServiceProvider?: ServiceProvider;
                  };
                } & { error: Error }
              ) => {
                setBusy(false);
                if (response.error) {
                  setRemoteErrors(response.error.message);
                  return;
                }
                if (response.data) {
                  onAccountAdded(response.data.account, {
                    newServiceProvider: response.data.newServiceProvider,
                  });
                  return;
                }
              },
              (err: Error) => {
                console.warn({ err });
                setBusy(false);
                setRemoteErrors("Operation failed. Please try again or contact customer support.");
              }
            );
          },
    });
  }, [updateTemplate, serviceProvider, account, onAccountAdded, record, setBusy, saveAccount]);

  const onAVBTChange = useOnValueChange(update, "assetsValueAboveThreshold");
  // tsx
  console.log("QuilterAccountFields", record);

  return (
    <>
      <FormField halfWidthByItself label="Head Account Number">
        <TextInput
          name="accountNumber"
          value={record.accountNumber ?? ""}
          onChange={onChange}
          error={errors?.accountNumber}
        />
      </FormField>

      <FormField halfWidth label="Account type">
        <SelectInput
          name="accountType"
          value={record.accountType ?? ""}
          onValueChange={onAccountTypeValueChange}
          error={errors?.accountType}
          options={QuilterAccountTypeOptions}
        />
      </FormField>

      <FormField halfWidthByItself label="Sub Account Number">
        <TextInput
          name="holderNumber"
          value={record.holderNumber ?? ""}
          onChange={onChange}
          error={errors?.holderNumber}
        />
      </FormField>

      {record.accountType && (
        <FormField label="Next steps">
          <AccountNextStepInput
            onValueChange={onAccountNextStepsValueChange}
            value={record.nextSteps ?? ""}
            serviceProviderType={ServiceProviderType.Insurance}
            error={errors?.nextSteps}
            fields={nextActionByQuilterAccountType(record.accountType)}
          />
        </FormField>
      )}

      <FormField label={<span>Did the death occur in the UK?</span>}>
        <YesNoBooleanRadioField
          value={record.isPlaceOfDeathUk}
          onValueChange={onPlaceOfDeathUkChange}
          error={errors?.isPlaceOfDeathUk}
          yesLabel="Yes death occurred in the UK"
          noLabel="No death occurred outside the UK"
        />
      </FormField>

      {record?.isPlaceOfDeathUk && (
        <FormField>
          <BooleanInput
            label={
              <span>
                Do you expect the value of the total assets held to be worth more than £100,000?
              </span>
            }
            value={record.assetsValueAboveThreshold}
            onValueChange={onAVBTChange}
          />
        </FormField>
      )}

      <Snackbar
        sx={{ top: "58px" }}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={!!remoteErrors}
        autoHideDuration={6000}
        onClose={() => setRemoteErrors(undefined)}
      >
        <Alert
          elevation={6}
          variant="filled"
          severity="error"
          onClose={() => setRemoteErrors(undefined)}
        >
          {remoteErrors}
        </Alert>
      </Snackbar>
    </>
  );
};
